import { Cart } from "@commercetools/platform-sdk";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { updateTOSessionWithPromotions } from "@/clients/talon-one/cart";
import {
  CashbackTransactions,
  CustomerLoyaltyPrograms,
  getCashbackTransactions,
  getLoyaltyPrograms,
} from "@/clients/talon-one/customer";
import { useCart } from "@/features/basket/hooks/use-cart";

const setUpdateTOSessionWithCashback = async (
  cart: Cart | null | undefined,
  cashbackAmountUsed: number,
) => {
  if (!cart) return;

  const promotions = { cashbackAmountUsed };
  const updatedPromotionResult = await updateTOSessionWithPromotions({ cart, promotions });
  return updatedPromotionResult?.cart;
};

const getCustomerAvailableCashback = async (customerId?: string) => {
  if (!customerId) return 0;

  const loyaltyPrograms = await getLoyaltyPrograms(customerId);
  return loyaltyPrograms.cashbackWallet?.ledger.currentBalance ?? 0;
};

const getCustomerLoyaltyPrograms = async (customerId?: string) => {
  if (!customerId) return {} as CustomerLoyaltyPrograms;

  return getLoyaltyPrograms(customerId);
};

export function calculateAppliedCashback(cart: Cart) {
  const appliedCashback = cart.custom?.fields?.cashbackAmountUsed;
  if (!appliedCashback) return 0;

  const appliedCashbackAmount =
    appliedCashback.centAmount / Math.pow(10, appliedCashback.fractionDigits);
  return appliedCashbackAmount;
}

export function useAvailableCashback(customerId: string) {
  return useQuery<number, Error>(["cashbackAmount"], () =>
    getCustomerAvailableCashback(customerId),
  );
}

export function useSetCashbackUsed() {
  const queryClient = useQueryClient();

  const { data: cart } = useCart();

  return useMutation(
    (cashbackAmountUsed: number) => setUpdateTOSessionWithCashback(cart, cashbackAmountUsed),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["CART"] });
      },
      onError: (err: Error) => {
        toast.error(`An error occurred ${err.message}`);
      },
    },
  );
}

export function useLoyaltyPrograms(customerId?: string, enabled: boolean = true) {
  return useQuery<CustomerLoyaltyPrograms, Error>({
    queryKey: ["loyaltyPrograms", customerId],
    queryFn: () => getCustomerLoyaltyPrograms(customerId!),
    enabled: enabled,
  });
}

export function useCashbackTransactions(customerId: string, skip: number) {
  return useQuery<CashbackTransactions, Error>(["cashbackTransactions", skip], () =>
    getCashbackTransactions(customerId, 10, skip),
  );
}
